import { Any } from "@cede/types";
import { constructNestedObjectFromFlatObject, getNestedObjectFieldValue } from "@cede/utils";
import {
  ARRAY_MARKER,
  ARRAY_SEPARATOR,
  OPTIONAL_MARKER,
  STORE_API_METHODS,
  StoreMethodMetaData,
} from "../../constants/methods";
import { WIDGET_CONFIGS, WidgetConfigMetaData } from "../../constants/widgetConfig";
import { ParamsFormsState } from "./useParamsForms";

/**
 * @description Get the default value for a param based on its type
 */
function getDefaultValue(type: string): Any {
  const defaultValuesByTypes: Record<string, Any> = {
    string: "",
    number: 0,
    boolean: false,
  };

  if (type in defaultValuesByTypes) return defaultValuesByTypes[type];

  if (type.includes(ARRAY_MARKER) && !type.includes(OPTIONAL_MARKER)) return [];

  return undefined;
}

/**
 * @description Cast a value to the correct type
 *
 * All the optional param with empty value are casted to undefined
 */
export const castValue = (value: Any, type: string) => {
  let castedValue = value;

  // Cast empty string to undefined if the param is optional whatever the type
  if (type.includes(OPTIONAL_MARKER) && value === "") return undefined;

  // Cast empty array to undefined if the param is optional
  if (Array.isArray(value) && !value.length && type.includes(OPTIONAL_MARKER)) {
    return undefined;
  }

  // Cast the string value to array type
  if (type.includes(ARRAY_MARKER) && typeof value === "string") {
    const items: Any[] = value ? value.split(ARRAY_SEPARATOR) : [];
    castedValue = items.map((item) => castValue(item, type.replace(ARRAY_MARKER, "")));
  } else if (type.includes("number")) {
    const valueAsNumber = Number(castedValue);

    if (isNaN(valueAsNumber)) throw new Error(`${value} is not a valid number`);

    castedValue = valueAsNumber;
  } else if (type.includes("boolean")) {
    castedValue = typeof castedValue === "boolean" ? castedValue : castedValue === "true";
  } else if (type.includes("string") && type.includes(OPTIONAL_MARKER) && value === "") {
    castedValue = undefined;
  }
  return castedValue;
};

/**
 * @description Cast params to the correct type and construct the object expected by the api
 */
const convertFormParamsToApiParams = (
  params: Record<string, Any>,
  method: StoreMethodMetaData | WidgetConfigMetaData,
) => {
  const methodParams = method.params;
  if (!methodParams) return {};

  const castedParams = Object.keys(params).reduce((acc, key) => {
    const param = params[key];

    const type = methodParams[key];

    if (param === undefined || !type) return acc;

    acc[key] = param;

    return acc;
  }, {} as Record<string, Any>);

  if (!method.apiParamsMapping) return castedParams;

  const apiParamsMapping = method.apiParamsMapping;

  return constructNestedObjectFromFlatObject(apiParamsMapping, castedParams);
};

/**
 * @description Get the initial state for a param
 */
function getInitialFormState(method: StoreMethodMetaData | WidgetConfigMetaData) {
  if (!method.params) return {};
  const paramState = Object.fromEntries(
    Object.keys(method.params).map((p) => {
      const type = method.params?.[p] || "string";
      return [p, getDefaultValue(type)];
    }),
  );

  if (!method.apiParamsMapping) return paramState;

  return convertFormParamsToApiParams(paramState, method);
}

/**
 * @description Get the initial state for all the forms
 */
export function getInitialFormStateForAllMethods() {
  return [...Object.values(STORE_API_METHODS), ...Object.values(WIDGET_CONFIGS)]
    .flat()
    .reduce<ParamsFormsState["forms"]>(
      (acc, method) => ({ ...acc, [method.methodName]: getInitialFormState(method) }),
      {} as ParamsFormsState["forms"],
    );
}

/**
 * @description Get the value of a param from the state object (Sometimes the param is nested in the state object)
 */
export function getFormValue(method: StoreMethodMetaData | WidgetConfigMetaData, paramName: string, form: Any) {
  const apiParamMapping = method.apiParamsMapping?.[paramName];
  if (!apiParamMapping) return form[paramName];
  return getNestedObjectFieldValue(form, apiParamMapping);
}

/**
 * @description Check if the value is valid based on the type
 */
function validateType(type: string, value: Any): boolean {
  if (type.includes(OPTIONAL_MARKER) && !value) return true;

  if (type.includes(ARRAY_MARKER))
    return Array.isArray(value) && value.every((v) => validateType(type.replace(ARRAY_MARKER, ""), v));

  if (type.includes("number")) return !isNaN(Number(value));

  if (type.includes("boolean")) return typeof value === "boolean";

  if (type.includes("string")) return typeof value === "string";

  return true;
}

/**
 * @description Check if the state is valid.
 * To be valid, the state must have all the required params for
 * each method and the value of each param must be of the correct type
 */
export function checkStateValidity(state: ParamsFormsState) {
  const methodDefinitions = [...Object.values(STORE_API_METHODS), ...Object.values(WIDGET_CONFIGS)].flat();

  return methodDefinitions.every((methodDefinition) => {
    const method = methodDefinition.methodName;
    const form = state.forms[method];

    if (!form) return false;
    const params = methodDefinition.params || {};

    if (!Object.keys(params).length) return true;

    return Object.entries(params).every(([param, type]) => {
      const value = getFormValue(methodDefinition, param, form);
      return validateType(type, value);
    });
  });
}
